import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Search, Settings as SettingsIcon, FileDown, Users, Plus, Trash2, Loader2, Sparkles, X, BrainCircuit } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Select } from "@/components/FormBits";
import { api, API } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { ADMIN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["", "Draft", "Submitted", "Approved", "Published", "Archived"];
const TYPE_CHIPS = [
  { key: "", label: "All" },
  { key: "project", label: "Projects" },
  { key: "award", label: "Awards" },
  { key: "certification", label: "Certifications" },
  { key: "event", label: "Events" },
  { key: "media", label: "Media & News" },
];
const SAVED_VIEWS = [
  { key: "award_wins", label: "Award wins" },
  { key: "iso_certs", label: "ISO / Certifications" },
  { key: "carbon", label: "Carbon" },
  { key: "social_value", label: "Social value" },
  { key: "safety", label: "Safety" },
  { key: "innovation", label: "Innovation" },
  { key: "bridges", label: "Bridge projects" },
  { key: "drainage", label: "Drainage & flood" },
  { key: "bid_ready", label: "Bid-ready" },
];
const TYPE_LABELS = {
  project: "Project",
  award: "Award",
  certification: "Cert",
  event: "Event",
  media: "Media",
};

function entryTitle(p) {
  if (p.project_name) return p.project_name;
  const td = p.type_data || {};
  return td.title || td.award_name || td.event_name || td.certification_name || td.initiative_name || "Untitled";
}

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [entryType, setEntryType] = useState("");
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [savedView, setSavedView] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    if (mine) params.mine = true;
    if (entryType) params.entry_type = entryType;
    if (savedView) params.saved_view = savedView;
    if (yearFrom) params.year_from = yearFrom;
    if (yearTo) params.year_to = yearTo;
    try {
      const { data } = await api.get("/admin/projects", { params });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
  }, [status, q, mine, entryType, savedView, yearFrom, yearTo]);

  const askLibrary = async () => {
    const query = aiQuery.trim();
    if (!query) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/library/parse-query", { query });
      const f = data?.filters || {};
      setQ(f.q || "");
      setEntryType(f.entry_type || "");
      setSavedView(f.saved_view || "");
      setYearFrom(f.year_from || "");
      setYearTo(f.year_to || "");
      // Build a human-readable summary
      const bits = [];
      if (f.saved_view) bits.push((SAVED_VIEWS.find((v) => v.key === f.saved_view) || {}).label || f.saved_view);
      if (f.entry_type) bits.push(f.entry_type);
      if (f.discipline) bits.push(f.discipline);
      if (f.year_from || f.year_to) bits.push(`${f.year_from || "any"}–${f.year_to || "any"}`);
      if (f.q) bits.push(`"${f.q}"`);
      setAiSummary(bits.length ? `Interpreted as: ${bits.join(" · ")}` : "No filters matched — showing all.");
    } catch (e) {
      toast.error("Couldn't parse that — try simpler wording");
    } finally {
      setAiBusy(false);
    }
  };

  const clearAllFilters = () => {
    setQ("");
    setEntryType("");
    setSavedView("");
    setYearFrom("");
    setYearTo("");
    setStatus("");
    setMine(false);
    setAiQuery("");
    setAiSummary("");
  };

  const activeFilterCount = [q, entryType, savedView, yearFrom, yearTo, status, mine].filter(Boolean).length;

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/blocked-submissions").then((r) => setBlocked(r.data)).catch(() => {});
  }, [isAdmin]);

  const [avatars, setAvatars] = useState({});
  useEffect(() => {
    api.get("/users/avatars").then((r) => setAvatars(r.data || {})).catch(() => {});
  }, []);

  const onExport = async () => {
    const token = localStorage.getItem("rcw_token");
    const url = `${API}/admin/projects.csv`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "rcwilliams-projects.csv";
    a.click();
  };

  // ---- bulk selection ----
  const deletable = items.filter((p) => isAdmin || p.owner_user_id === user?.id);
  const allDeletableIds = deletable.map((p) => p.id);
  const allSelected = allDeletableIds.length > 0 && allDeletableIds.every((id) => selected.has(id));
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allDeletableIds));
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} project${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/projects/bulk-delete", { ids });
      const n = (data?.deleted || []).length;
      const skipped = (data?.skipped || []).length;
      toast.success(`Deleted ${n} project${n === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""}`);
      setSelected(new Set());
      fetchData();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const submitted = items.filter((i) => i.status === "Submitted").length;
    const drafts = items.filter((i) => i.status?.startsWith("Draft")).length;
    const published = items.filter((i) => i.status === "Published").length;
    return { total, submitted, drafts, published };
  }, [items]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="rcw-label">{isAdmin ? "Admin dashboard" : "Project portal"}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1 text-gray-900">
              Projects
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {isAdmin ? "Manage every submission across the team." : "Submit your own and browse the team's work."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/" className="rcw-btn-amber text-sm" data-testid="new-project-btn">
              <Plus className="h-4 w-4" /> New project
            </Link>
            {isAdmin && (
              <>
                <Link to="/admin/users" className="rcw-btn-ghost text-sm" data-testid="admin-users-link">
                  <Users className="h-4 w-4" /> Users
                </Link>
                <Link to="/admin/knowledge" className="rcw-btn-ghost text-sm" data-testid="admin-knowledge-link">
                  <BrainCircuit className="h-4 w-4" /> AI Training
                </Link>
                <Link to="/admin/settings" className="rcw-btn-ghost text-sm" data-testid={ADMIN.settingsLink}>
                  <SettingsIcon className="h-4 w-4" /> Settings
                </Link>
                <button onClick={onExport} className="rcw-btn-ghost text-sm" data-testid={ADMIN.exportCsv}>
                  <FileDown className="h-4 w-4" /> Export CSV
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="Total" value={stats.total} />
          <Stat label="Submitted" value={stats.submitted} accent />
          <Stat label="Drafts" value={stats.drafts} />
          <Stat label="Published" value={stats.published} />
        </div>

        {isAdmin && <BlockedCard data={blocked} />}

        {/* AI library query bar (Phase 2B) */}
        <div className="rcw-card mt-6 p-4 sm:p-5 border-amber-200 bg-gradient-to-r from-amber-50/60 to-white">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <div className="text-xs uppercase tracking-widest font-bold text-amber-700">Ask the library</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") askLibrary(); }}
              placeholder='e.g. "show me carbon projects" · "find bridge award wins" · "ISO certificates"'
              className="rcw-input flex-1"
              data-testid="library-ai-query"
            />
            <button
              onClick={askLibrary}
              disabled={aiBusy || !aiQuery.trim()}
              className="rcw-btn-amber text-sm sm:w-auto"
              data-testid="library-ai-ask"
            >
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Ask
            </button>
          </div>
          {aiSummary && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600" data-testid="library-ai-summary">
              <span>{aiSummary}</span>
              <button onClick={clearAllFilters} className="text-amber-700 underline underline-offset-2">clear</button>
            </div>
          )}
        </div>

        <div className="rcw-card mt-6 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search project, client, location, submitter…"
                className="rcw-input pl-9"
                data-testid={ADMIN.search}
              />
            </div>
            <div className="sm:w-56">
              <Select
                options={STATUSES.filter(Boolean)}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid={ADMIN.filterStatus}
              />
            </div>
            <button
              onClick={() => setStatus(status === "Submitted" ? "" : "Submitted")}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors ${
                status === "Submitted"
                  ? "bg-amber-400 border-amber-400 text-gray-900"
                  : "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
              }`}
              data-testid="needs-review-filter"
            >
              Needs review
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-white/70">{stats.submitted}</span>
            </button>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mine}
                onChange={(e) => setMine(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                data-testid="filter-mine"
              />
              Mine only
            </label>
          </div>

          {/* Type filter chips */}
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="type-filter-chips">
            {TYPE_CHIPS.map((t) => {
              const active = entryType === t.key;
              return (
                <button
                  key={t.key || "all"}
                  onClick={() => setEntryType(t.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    active
                      ? "bg-gray-900 text-amber-300 border-gray-900"
                      : "bg-white text-gray-700 border-gray-200 hover:border-amber-300"
                  }`}
                  data-testid={`type-chip-${t.key || "all"}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Saved-view chips (Phase 2B) */}
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="saved-view-chips">
            {SAVED_VIEWS.map((v) => {
              const active = savedView === v.key;
              return (
                <button
                  key={v.key}
                  onClick={() => setSavedView(active ? "" : v.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    active
                      ? "bg-amber-400 text-gray-900 border-amber-400 font-semibold"
                      : "bg-white text-gray-600 border-dashed border-gray-300 hover:border-amber-300 hover:text-gray-900"
                  }`}
                  data-testid={`saved-view-${v.key}`}
                >
                  {v.label}
                </button>
              );
            })}
            {savedView && (
              <button
                onClick={() => setSavedView("")}
                className="text-xs px-2 py-1.5 rounded-full text-red-500 hover:bg-red-50 inline-flex items-center gap-1"
                data-testid="saved-view-clear"
              >
                <X className="h-3 w-3" /> clear view
              </button>
            )}
          </div>

          {/* Year range mini-filter */}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span>Year range:</span>
            <input
              type="number"
              min="1990"
              max="2100"
              placeholder="from"
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
              className="w-20 rcw-input py-1 px-2 text-xs"
              data-testid="filter-year-from"
            />
            <span>–</span>
            <input
              type="number"
              min="1990"
              max="2100"
              placeholder="to"
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
              className="w-20 rcw-input py-1 px-2 text-xs"
              data-testid="filter-year-to"
            />
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="ml-auto text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
                data-testid="clear-all-filters"
              >
                <X className="h-3 w-3" /> Clear all ({activeFilterCount})
              </button>
            )}
          </div>


          <div className="mt-4 overflow-x-auto" data-testid={ADMIN.table}>
            {selected.size > 0 && (
              <div className="mb-3 flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="bulk-action-bar">
                <div className="text-sm text-amber-900">
                  <b>{selected.size}</b> selected
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelected(new Set())} className="rcw-btn-ghost text-xs">
                    Clear
                  </button>
                  <button
                    onClick={bulkDelete}
                    disabled={bulkBusy}
                    className="rcw-btn-ghost text-xs text-red-600 hover:bg-red-50"
                    data-testid="bulk-delete-btn"
                  >
                    {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Delete selected
                  </button>
                </div>
              </div>
            )}
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                      title={allSelected ? "Clear all" : "Select all"}
                      data-testid="select-all-checkbox"
                      disabled={allDeletableIds.length === 0}
                    />
                  </Th>
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th>Title</Th>
                  <Th>Client</Th>
                  <Th>Location</Th>
                  <Th>Status</Th>
                  <Th>Submitted by</Th>
                  <Th>Public use</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-gray-500">Loading…</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-10 text-center text-gray-500">No submissions found</td>
                  </tr>
                ) : (
                  items.map((p) => {
                    const isOwner = p.owner_user_id === user?.id;
                    const canDelete = isAdmin || isOwner;
                    const et = (p.entry_type || "project").toLowerCase();
                    return (
                      <tr key={p.id} className={`border-b border-gray-100 hover:bg-gray-50/60 ${selected.has(p.id) ? "bg-amber-50/40" : ""}`}>
                        <Td>
                          {canDelete && (
                            <input
                              type="checkbox"
                              checked={selected.has(p.id)}
                              onChange={() => toggleOne(p.id)}
                              className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                              data-testid={`select-row-${p.id}`}
                            />
                          )}
                        </Td>
                        <Td className="text-gray-500">{(p.submitted_at || p.created_at || "").slice(0, 10)}</Td>
                        <Td><TypeBadge type={et} /></Td>
                        <Td className="font-medium text-gray-900">
                          {entryTitle(p)}
                          {isOwner && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-600 font-bold">Mine</span>}
                        </Td>
                        <Td>{p.client_name || (p.type_data?.organisation) || (p.type_data?.publication) || "—"}</Td>
                        <Td>{p.location || (p.type_data?.location) || "—"}</Td>
                        <Td><StatusBadge status={p.status} /></Td>
                        <Td>
                          <div className="flex items-center gap-2">
                            <Avatar name={p.submitter_name} src={avatars[p.owner_user_id]} size="xs" data-testid={`submitter-avatar-${p.id}`} />
                            <span>{p.submitter_name || "—"}</span>
                          </div>
                        </Td>
                        <Td>{p.public_use || "—"}</Td>
                        <Td className="text-right">
                          <Link
                            to={`/admin/projects/${p.id}`}
                            className="rcw-btn-ghost py-1.5 px-2 text-xs"
                            data-testid={ADMIN.rowView(p.id)}
                          >
                            <Eye className="h-3.5 w-3.5" /> View
                          </Link>
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-xs font-semibold uppercase tracking-wider py-3 px-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`py-3 px-3 align-middle ${className}`}>{children}</td>;
}
function Stat({ label, value, accent }) {
  return (
    <div className="rcw-card p-4">
      <div className="rcw-label">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold ${accent ? "text-amber-600" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const map = {
    Draft: "bg-gray-100 text-gray-700",
    Submitted: "bg-amber-50 text-amber-700",
    Approved: "bg-blue-50 text-blue-700",
    Published: "bg-emerald-50 text-emerald-700",
    Archived: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status || "—"}
    </span>
  );
}

function BlockedCard({ data }) {
  if (!data) return null;
  const c24 = data.counts?.["24h"] || {};
  const c7 = data.counts?.["7d"] || {};
  const c30 = data.counts?.["30d"] || {};
  const sum = (o) => Object.values(o).reduce((a, b) => a + b, 0);
  const labels = { honeypot: "Honeypot", too_fast: "Too fast", rate_limit: "Rate limit", other: "Other" };
  const reasons = ["honeypot", "too_fast", "rate_limit"];

  return (
    <div className="rcw-card mt-4 p-4 sm:p-5" data-testid="blocked-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="rcw-label">Bot protection</div>
          <div className="font-display text-base sm:text-lg font-semibold text-gray-900">Blocked submissions</div>
        </div>
        <div className="flex gap-3 text-xs">
          <Period label="24h" total={sum(c24)} />
          <Period label="7d" total={sum(c7)} />
          <Period label="30d" total={sum(c30)} />
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {reasons.map((r) => (
          <div key={r} className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{labels[r]}</div>
            <div className="mt-0.5 text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{c24[r] || 0}</span>
              <span className="text-gray-400"> · {c7[r] || 0} · {c30[r] || 0}</span>
            </div>
          </div>
        ))}
      </div>
      {(data.recent || []).length > 0 ? (
        <details className="mt-3" data-testid="blocked-recent">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
            Recent {Math.min(data.recent.length, 50)} blocked attempts
          </summary>
          <ul className="mt-2 text-xs divide-y divide-gray-100">
            {data.recent.slice(0, 20).map((it, i) => (
              <li key={i} className="py-1.5 flex justify-between gap-2">
                <span className="text-gray-500">{(it.ts || "").slice(0, 19).replace("T", " ")}</span>
                <span className="text-gray-700">{labels[it.reason] || it.reason}</span>
                <span className="text-gray-400 truncate max-w-[160px]">{it.ip}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <div className="mt-3 text-[11px] text-gray-400">
        Numbers shown as 24h · 7d · 30d. Three layers: invisible honeypot field, &lt;6s fill check, 5/15min IP rate limit.
      </div>
    </div>
  );
}

function Period({ label, total }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="font-display text-lg font-semibold text-gray-900">{total}</div>
    </div>
  );
}

function TypeBadge({ type }) {
  const styles = {
    project: "bg-amber-100 text-amber-800 border-amber-200",
    award: "bg-yellow-100 text-yellow-800 border-yellow-200",
    certification: "bg-emerald-100 text-emerald-800 border-emerald-200",
    event: "bg-sky-100 text-sky-800 border-sky-200",
    media: "bg-rose-100 text-rose-800 border-rose-200",
  };
  const cls = styles[type] || "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}>
      {TYPE_LABELS[type] || "Project"}
    </span>
  );
}


